import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useCurrentUser } from 'store/auth';
import { APP } from 'config';
import { ClientFeature } from 'store/admin-clients';
import { AdminPermission } from 'store/admin-admins';
import { PermissionName } from 'store/permissions';
import { SUPPORTED_TAX_EXEMPTION_COUNTRIES } from 'store/shop';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
  PermissionLevels,
} from '../constants/Administrators';
import { Permission, UseMe } from '../types/Administrators';

const initalMeValues: UseMe = {
  loading: false,
  globalPermissions: [],
  localPermissions: [],
  adminPermissions: [],
  getPermissionsByModule: () => [],
  checkIfCanEdit: () => false,
  checkIfCanView: () => false,
  canEditGroups: false,
  canEditAdministrators: false,
  canEditGeneralSettings: false,
  canEditCustomEmployeeCategory: false,
  canEditCompanies: false,
  canEditEmployees: false,
  canEditBenefits: false,
  canEditNews: false,
  hasBenefitPermissions: false,
  hasEmployeePermissions: false,
  hasAdministratorPermissions: false,
  hasGroupPermissions: false,
  hasNewsPermissions: false,
  hasOrganisationsPermissions: false,
  isRoot: false,
  isMelpAdmin: false,
  isHrAdmin: false,
  isFeatureEnabled: () => false,
  canViewAdvancedPermissions: false,
  hasViewPermission: () => false,
  hasEditPermission: () => false,
  hasPermissionScope: false,
  taxExemptionCountries: [],
};

const meContext = createContext<UseMe>(initalMeValues);

const useProvideMe = (): UseMe => {
  const { data: me, isLoading: apiLoading } = useCurrentUser();

  const isRoot = useMemo(() => me?.root, [me]);
  const isMelpAdmin = useMemo(() => me?.role === 'melpAdmin', [me]);
  const isHrAdmin = useMemo(() => me?.role === 'hrAdmin', [me]);

  const globalPermissions = useMemo<Permission[]>(() => {
    if (!me?.permissions) return [];
    return me?.permissions.filter(
      (permission) => permission.level === PermissionLevels.Global,
    );
  }, [me]);

  const localPermissions = useMemo<Permission[]>(() => {
    if (!me?.permissions) return [];
    return me?.permissions.filter(
      (permission) => permission.level === PermissionLevels.Local,
    );
  }, [me]);

  const adminPermissions = useMemo<AdminPermission[]>(() => {
    return me?.adminPermissions ?? [];
  }, [me]);

  const getPermissionsByModule = useCallback(
    (
      module:
        | LocalPermissionModules
        | GlobalPermissionModules
        | Array<LocalPermissionModules | GlobalPermissionModules>,
    ): Permission[] => {
      const modules = Array.isArray(module) ? module : [module];
      if (!me?.permissions) return [];
      return me?.permissions.filter((permission) =>
        modules?.includes(permission.module),
      );
    },
    [me],
  );

  const checkIfCanEdit = useCallback(
    (module: LocalPermissionModules | GlobalPermissionModules) => {
      if (isRoot) return true;
      if (me?.parentCompany?.features.includes('advancedPermissions')) {
        return true;
      }
      const permissions = getPermissionsByModule(module);
      if (permissions.length === 0) return false;
      return permissions.some((permission) => permission.permission === 'edit');
    },
    [getPermissionsByModule, isRoot, me],
  );

  const checkIfCanView = useCallback(
    (module: LocalPermissionModules | GlobalPermissionModules) => {
      if (isRoot) return true;
      if (me?.parentCompany?.features.includes('advancedPermissions')) {
        return true;
      }
      const permissions = getPermissionsByModule(module);
      if (permissions.length === 0) return false;
      return permissions.some((permission) => permission.permission === 'view');
    },
    [getPermissionsByModule, isRoot, me],
  );

  const canEditNews = useMemo(
    () => checkIfCanEdit(LocalPermissionModules.News),
    [checkIfCanEdit],
  );

  const canViewNews = useMemo(
    () => checkIfCanView(LocalPermissionModules.News),
    [checkIfCanView],
  );

  const hasNewsPermissions = useMemo(
    () => canEditNews || canViewNews,
    [canEditNews, canViewNews],
  );

  const canEditBenefits = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.Benefits),
    [checkIfCanEdit],
  );

  const canViewBenefits = useMemo(
    () => checkIfCanView(GlobalPermissionModules.Benefits),
    [checkIfCanView],
  );

  const hasBenefitPermissions = useMemo(
    () => canEditBenefits || canViewBenefits,
    [canEditBenefits, canViewBenefits],
  );

  const canEditEmployees = useMemo(
    () => checkIfCanEdit(LocalPermissionModules.Employees),
    [checkIfCanEdit],
  );

  const canViewEmployees = useMemo(
    () => checkIfCanView(LocalPermissionModules.Employees),
    [checkIfCanView],
  );

  const hasEmployeePermissions = useMemo(
    () => canEditEmployees || canViewEmployees,
    [canEditEmployees, canViewEmployees],
  );

  const canEditAdministrators = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.Administrators),
    [checkIfCanEdit],
  );

  const canEditGeneralSettings = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.OrganisationStructure),
    [checkIfCanEdit],
  );

  const canEditCustomEmployeeCategory = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.OrganisationStructure),
    [checkIfCanEdit],
  );

  const canEditCompanies = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.OrganisationStructure),
    [checkIfCanEdit],
  );

  const canViewCompanySettings = useMemo(
    () => checkIfCanView(GlobalPermissionModules.OrganisationStructure),
    [checkIfCanView],
  );

  const hasOrganisationsPermissions = useMemo(
    () => canViewCompanySettings || canEditCustomEmployeeCategory,
    [canEditCustomEmployeeCategory, canViewCompanySettings],
  );

  const canViewAdministrators = useMemo(
    () => checkIfCanView(GlobalPermissionModules.Administrators),
    [checkIfCanView],
  );

  const hasAdministratorPermissions = useMemo(
    () => canEditAdministrators || canViewAdministrators,
    [canEditAdministrators, canViewAdministrators],
  );

  const canEditGroups = useMemo(
    () => checkIfCanEdit(GlobalPermissionModules.EmployeeGroups),
    [checkIfCanEdit],
  );

  const canViewGroups = useMemo(
    () => checkIfCanView(GlobalPermissionModules.EmployeeGroups),
    [checkIfCanView],
  );

  const hasGroupPermissions = useMemo(
    () => canEditGroups || canViewGroups,
    [canEditGroups, canViewGroups],
  );

  const isFeatureEnabled = (featureKey: ClientFeature) =>
    me?.parentCompany?.features.includes(featureKey) ?? false;

  const canViewAdvancedPermissions =
    isFeatureEnabled('advancedPermissions') ||
    isFeatureEnabled('advancedPermissionsView');

  const checkPermissionAccess = useCallback(
    (
      permissions: readonly PermissionName[] | undefined,
      minAccessLevel: number,
    ) => {
      if (
        !permissions?.length ||
        !me?.parentCompany?.features.includes('advancedPermissions')
      )
        return true;

      return permissions.some((requiredPermission) =>
        (me?.adminRole?.permissions ?? []).some(
          (rolePermission) =>
            rolePermission.permission === requiredPermission &&
            rolePermission.accessLevel >= minAccessLevel,
        ),
      );
    },
    [me],
  );

  const hasViewPermission = useCallback(
    (permissions?: readonly PermissionName[]) =>
      checkPermissionAccess(permissions, 1),
    [checkPermissionAccess],
  );

  const hasEditPermission = useCallback(
    (permissions?: readonly PermissionName[]) =>
      checkPermissionAccess(permissions, 2),
    [checkPermissionAccess],
  );

  const taxExemptionCountries = useMemo(() => {
    const allCountries = me?.parentCompany
      ? [
          me.parentCompany.country,
          ...me.parentCompany.companies.map((company) => company.country),
        ]
      : [];

    return SUPPORTED_TAX_EXEMPTION_COUNTRIES.filter((country) =>
      allCountries.includes(country),
    );
  }, [me]);

  const hasPermissionScope = useMemo(
    () => (me?.permissionScopes.length ?? 0) > 0,
    [me],
  );

  return {
    me,
    loading: apiLoading,
    globalPermissions,
    localPermissions,
    adminPermissions,
    getPermissionsByModule,
    checkIfCanEdit,
    checkIfCanView,
    canEditGroups,
    canEditAdministrators,
    canEditGeneralSettings,
    canEditCustomEmployeeCategory,
    canEditCompanies,
    canEditEmployees,
    canEditBenefits,
    canEditNews,
    hasBenefitPermissions,
    hasEmployeePermissions,
    hasAdministratorPermissions,
    hasGroupPermissions,
    hasNewsPermissions,
    hasOrganisationsPermissions,
    isRoot,
    isMelpAdmin,
    isHrAdmin,
    isFeatureEnabled,
    canViewAdvancedPermissions,
    hasViewPermission,
    hasEditPermission,
    hasPermissionScope,
    taxExemptionCountries,
  };
};

export const MeProvider = ({ children }: PropsWithChildren<{}>) => {
  const me = useProvideMe();
  return <meContext.Provider value={me}>{children}</meContext.Provider>;
};

export const useMe = (): UseMe => {
  return useContext(meContext);
};

export const useCompanyLanguages = () => {
  const { me } = useMe();

  const defaultLanguage = me?.parentCompany?.settings.defaultLanguage ?? 'EN';
  const supportedLanguages = me?.parentCompany?.supportedLanguages ?? [];

  return {
    defaultLanguage,
    supportedLanguages: [
      defaultLanguage,
      ...supportedLanguages
        .filter((language) => language !== defaultLanguage)
        .sort(),
    ],
    country: me?.parentCompany?.country,
  };
};

export const useDefaultCurrency = () => {
  const { me } = useMe();

  return {
    currency: me?.parentCompany?.defaultCurrency ?? APP.currencies.default,
  };
};
